import React from 'react';
import { CellPosition } from '../types';
import { getCellKey, selectRange } from '../utils';

interface UseSpreadsheetSelectionProps {
  onSelectionChange?: (selectedCells: CellPosition[]) => void;
}

export function useSpreadsheetSelection({ onSelectionChange }: UseSpreadsheetSelectionProps) {
  // Selection state - Initialize with A1 selected
  const [selectedCells, setSelectedCells] = React.useState<Set<string>>(() => new Set([getCellKey(0, 'A')]));
  const [isSelecting, setIsSelecting] = React.useState(false);
  const [selectionStart, setSelectionStart] = React.useState<CellPosition | null>(() => ({ rowIdx: 0, columnKey: 'A' }));
  const [activeCell, setActiveCell] = React.useState<CellPosition | null>(() => ({ rowIdx: 0, columnKey: 'A' }));

  // Handle cell mouse down
  const handleCellMouseDown = React.useCallback((
    rowIdx: number, 
    columnKey: string, 
    event: React.MouseEvent,
    isEditable: boolean
  ) => {
    if (columnKey === '__rowNumber') return;
    
    // Ignore right-clicks (context menu) - don't change selection
    if (event.button === 2) return;
    
    // Don't prevent default for editable cells to allow input focus
    if (!isEditable) {
      event.preventDefault();
    }
    
    const cellPosition = { rowIdx, columnKey };
    
    if (event.shiftKey && selectionStart) {
      // Shift+click for range selection
      const newSelectedCells = selectRange(selectionStart, cellPosition);
      setSelectedCells(newSelectedCells);
    } else if (event.ctrlKey || event.metaKey) {
      // Ctrl+click for multi-selection
      const cellKey = getCellKey(rowIdx, columnKey);
      const newSelectedCells = new Set(selectedCells);
      if (newSelectedCells.has(cellKey)) {
        newSelectedCells.delete(cellKey);
      } else {
        newSelectedCells.add(cellKey);
      }
      setSelectedCells(newSelectedCells);
      setSelectionStart(cellPosition);
    } else {
      // Regular click
      const cellKey = getCellKey(rowIdx, columnKey);
      setSelectedCells(new Set([cellKey]));
      setSelectionStart(cellPosition);
      setIsSelecting(true);
    }
    
    setActiveCell(cellPosition);
  }, [selectedCells, selectionStart]);

  // Handle cell mouse enter during selection
  const handleCellMouseEnter = React.useCallback((rowIdx: number, columnKey: string) => {
    if (isSelecting && selectionStart && columnKey !== '__rowNumber') {
      const cellPosition = { rowIdx, columnKey };
      const newSelectedCells = selectRange(selectionStart, cellPosition);
      setSelectedCells(newSelectedCells);
    }
  }, [isSelecting, selectionStart]);

  // Handle mouse up to end selection
  const handleMouseUp = React.useCallback(() => {
    setIsSelecting(false);
  }, []);

  // Navigate to a specific cell
  const navigateToCell = React.useCallback((position: CellPosition, shiftKey = false) => {
    if (shiftKey && selectionStart) {
      // Shift+arrow for range selection
      const newSelectedCells = selectRange(selectionStart, position);
      setSelectedCells(newSelectedCells);
    } else {
      // Regular navigation
      const cellKey = getCellKey(position.rowIdx, position.columnKey);
      setSelectedCells(new Set([cellKey]));
      setSelectionStart(position);
    }
    
    setActiveCell(position);
  }, [selectionStart]);

  // Update selection callback
  React.useEffect(() => {
    if (onSelectionChange) {
      const cellPositions: CellPosition[] = Array.from(selectedCells).map(cellKey => {
        const [rowIdx, columnKey] = cellKey.split('-');
        return { rowIdx: parseInt(rowIdx), columnKey };
      });
      onSelectionChange(cellPositions);
    }
  }, [selectedCells, onSelectionChange]);

  return {
    selectedCells,
    isSelecting,
    selectionStart,
    activeCell,
    setSelectedCells,
    setSelectionStart,
    setActiveCell,
    handleCellMouseDown,
    handleCellMouseEnter,
    handleMouseUp,
    navigateToCell
  };
} 